import React, { useState, useRef, useEffect, useCallback } from 'react'

/*
 * 손가락 이동을 그대로 오프셋에 더하면, 한 칸이 65px이라 33px만 움직여도
 * 카드가 넘어간다. 화면에서는 아주 작은 손짓이라 의도치 않게 넘어가고
 * 그때마다 3D 변형이 일어나 어지럽다. 이동량을 이 비율로 줄여
 * 한 장 넘기는 데 필요한 손가락 거리를 늘린다.
 */
const DRAG_RATIO = 0.55

interface SlideWidgetProps<T> {
  items: T[]
  renderItem: (
    item: T,
    index: number,
    style: React.CSSProperties,
    /** 가운데인지, 그 왼쪽인지 오른쪽인지 (좌우 카드를 다르게 그리는 데 쓴다) */
    position: 'left' | 'center' | 'right'
  ) => React.ReactNode
  itemWidth?: number
  spacingMultiplier?: number
  onIndexChange?: (index: number) => void
  loop?: boolean
  /** 슬라이드 뷰포트 높이 (영수증 등 가변 높이 카드용, 기본: 앱바·탭 제외 뷰포트) */
  viewportHeight?: string
  minViewportHeightPx?: number
}

const SlideWidget = <T,>({
  items,
  renderItem,
  itemWidth = 260,
  spacingMultiplier = 0.6,
  onIndexChange,
  loop = true,
  viewportHeight = 'calc(100dvh - 168px)',
  minViewportHeightPx = 520,
}: SlideWidgetProps<T>) => {
  const totalItemWidth = itemWidth * spacingMultiplier
  const initialOffset = 0

  const [currentOffset, setCurrentOffset] = useState(initialOffset)
  const [isDragging, setIsDragging] = useState(false)
  const [dragStartX, setDragStartX] = useState(0)
  const [dragStartOffset, setDragStartOffset] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(390)
  const animationFrameRef = useRef<number | null>(null)
  const currentOffsetRef = useRef(currentOffset)

  useEffect(() => {
    currentOffsetRef.current = currentOffset
  }, [currentOffset])

  useEffect(() => {
    const updateContainerWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth)
      } else {
        setContainerWidth(390)
      }
    }

    updateContainerWidth()
    const timeout = setTimeout(updateContainerWidth, 0)
    window.addEventListener('resize', updateContainerWidth)
    return () => {
      clearTimeout(timeout)
      window.removeEventListener('resize', updateContainerWidth)
    }
  }, [])

  const centerX = containerWidth / 2

  /*
   * 인덱스가 실제로 바뀔 때만 알린다.
   *
   * 예전엔 currentOffset이 바뀔 때마다 불러서, 스냅 애니메이션 300ms 동안
   * 프레임마다 부모의 setState가 돌았다. 같은 값이라 화면은 그대로지만
   * 넘기는 내내 부모가 다시 그려져 버벅임을 더했다.
   */
  const lastIndexRef = useRef<number | null>(null)
  useEffect(() => {
    if (!onIndexChange || items.length === 0) return

    const displayIndex = Math.round(-currentOffset / totalItemWidth)
    const normalizedIndex = loop
      ? ((displayIndex % items.length) + items.length) % items.length
      : Math.max(0, Math.min(displayIndex, items.length - 1))

    if (lastIndexRef.current === normalizedIndex) return
    lastIndexRef.current = normalizedIndex
    onIndexChange(normalizedIndex)
  }, [currentOffset, totalItemWidth, items.length, loop, onIndexChange])

  /**
   * 돌고 있던 스냅 애니메이션을 멈춘다.
   *
   * 안 멈추면 이전 애니메이션과 새 드래그가 같은 offset을 동시에 쓴다.
   * 빠르게 연달아 넘길 때 카드가 앞뒤로 튀던 원인이다.
   */
  const stopAnimation = () => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
  }

  /*
   * 시작 지점은 state가 아니라 ref에서 읽는다.
   *
   * 애니메이션 중에는 state가 한 프레임 뒤처져 있어, state를 쓰면 손을 대는
   * 순간 카드가 이전 위치로 한 번 튀었다.
   */
  const beginDrag = (clientX: number) => {
    stopAnimation()
    setIsDragging(true)
    setDragStartX(clientX)
    setDragStartOffset(currentOffsetRef.current)
    setCurrentOffset(currentOffsetRef.current)
  }

  const handleMouseDown = (e: React.MouseEvent) => beginDrag(e.clientX)

  const handleTouchStart = (e: React.TouchEvent) =>
    beginDrag(e.touches[0].clientX)

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return

      const deltaX = (e.clientX - dragStartX) * DRAG_RATIO
      let newOffset = dragStartOffset + deltaX

      // 시작 위치에서 한 칸 이상 이동하지 않도록 부드럽게 제한
      const startDisplayIndex = Math.round(-dragStartOffset / totalItemWidth)
      const startOffset = -startDisplayIndex * totalItemWidth
      const maxOffset = startOffset + totalItemWidth
      const minOffset = startOffset - totalItemWidth

      // 한 칸 범위를 넘어가면 저항감 적용
      if (newOffset > maxOffset) {
        const excess = newOffset - maxOffset
        const resistance = 1 / (1 + excess / totalItemWidth)
        newOffset = maxOffset + excess * resistance
      } else if (newOffset < minOffset) {
        const excess = minOffset - newOffset
        const resistance = 1 / (1 + excess / totalItemWidth)
        newOffset = minOffset - excess * resistance
      }

      // 한 칸 제한 범위 내에서는 loop 적용하지 않음
      // 한 칸 제한이 이미 적용되어 있으므로 loop 범위를 벗어날 수 없음
      if (!loop) {
        newOffset = Math.max(
          -(items.length - 1) * totalItemWidth,
          Math.min(0, newOffset)
        )
      }

      currentOffsetRef.current = newOffset
      setCurrentOffset(newOffset)
    },
    [
      isDragging,
      dragStartX,
      dragStartOffset,
      loop,
      items.length,
      totalItemWidth,
    ]
  )

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isDragging) return

      const deltaX = (e.touches[0].clientX - dragStartX) * DRAG_RATIO
      let newOffset = dragStartOffset + deltaX

      // 시작 위치에서 한 칸 이상 이동하지 않도록 부드럽게 제한
      const startDisplayIndex = Math.round(-dragStartOffset / totalItemWidth)
      const startOffset = -startDisplayIndex * totalItemWidth
      const maxOffset = startOffset + totalItemWidth
      const minOffset = startOffset - totalItemWidth

      // 한 칸 범위를 넘어가면 저항감 적용
      if (newOffset > maxOffset) {
        const excess = newOffset - maxOffset
        const resistance = 1 / (1 + excess / totalItemWidth)
        newOffset = maxOffset + excess * resistance
      } else if (newOffset < minOffset) {
        const excess = minOffset - newOffset
        const resistance = 1 / (1 + excess / totalItemWidth)
        newOffset = minOffset - excess * resistance
      }

      // 한 칸 제한 범위 내에서는 loop 적용하지 않음
      // 한 칸 제한이 이미 적용되어 있으므로 loop 범위를 벗어날 수 없음
      if (!loop) {
        newOffset = Math.max(
          -(items.length - 1) * totalItemWidth,
          Math.min(0, newOffset)
        )
      }

      currentOffsetRef.current = newOffset
      setCurrentOffset(newOffset)
    },
    [
      isDragging,
      dragStartX,
      dragStartOffset,
      loop,
      items.length,
      totalItemWidth,
    ]
  )

  const snapToNearest = useCallback(() => {
    if (items.length === 0) return

    const offset = currentOffsetRef.current
    const startDisplayIndex = Math.round(-dragStartOffset / totalItemWidth)
    const currentDisplayIndex = Math.round(-offset / totalItemWidth)

    // 시작 위치에서 한 칸 이상 이동하지 않도록 제한
    let targetDisplayIndex = currentDisplayIndex
    const diff = currentDisplayIndex - startDisplayIndex

    if (Math.abs(diff) > 1) {
      // 한 칸 이상 이동했다면 한 칸만 이동
      targetDisplayIndex = startDisplayIndex + (diff > 0 ? 1 : -1)
    }

    let targetOffset = -targetDisplayIndex * totalItemWidth
    const startOffset = offset
    let distance = targetOffset - startOffset

    // loop 모드에서 한 칸 이동 시 거리가 너무 크면 반대 방향으로 이동
    if (loop && items.length > 0 && Math.abs(distance) > totalItemWidth * 1.5) {
      // 반대 방향으로 한 칸 이동
      const alternativeOffset =
        distance > 0
          ? targetOffset - items.length * totalItemWidth
          : targetOffset + items.length * totalItemWidth
      const alternativeDistance = alternativeOffset - startOffset
      if (Math.abs(alternativeDistance) < Math.abs(distance)) {
        targetOffset = alternativeOffset
        distance = alternativeDistance
      }
    }

    if (!loop) {
      targetOffset = Math.max(
        -(items.length - 1) * totalItemWidth,
        Math.min(0, targetOffset)
      )
      distance = targetOffset - startOffset
    }

    // 이전 스냅이 아직 돌고 있으면 멈추고 시작한다
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }

    const duration = 300
    const startTime = Date.now()

    const animate = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)
      const easeOut = 1 - Math.pow(1 - progress, 3)
      const newOffset = startOffset + distance * easeOut

      setCurrentOffset(newOffset)
      currentOffsetRef.current = newOffset

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate)
      } else {
        animationFrameRef.current = null
      }
    }

    animationFrameRef.current = requestAnimationFrame(animate)
  }, [totalItemWidth, items.length, loop, dragStartOffset])

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
    snapToNearest()
  }, [snapToNearest])

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false)
    snapToNearest()
  }, [snapToNearest])

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
      window.addEventListener('touchmove', handleTouchMove)
      window.addEventListener('touchend', handleTouchEnd)
      return () => {
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseup', handleMouseUp)
        window.removeEventListener('touchmove', handleTouchMove)
        window.removeEventListener('touchend', handleTouchEnd)
      }
    }
  }, [
    isDragging,
    handleMouseMove,
    handleTouchMove,
    handleMouseUp,
    handleTouchEnd,
  ])

  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [])

  const getItemStyle = (displayIndex: number): React.CSSProperties => {
    const baseLeft = centerX - itemWidth / 2
    const itemX = baseLeft + displayIndex * totalItemWidth + currentOffset
    const itemCenterX = itemX + itemWidth / 2
    const distanceFromCenter = itemCenterX - centerX
    const slideSize = totalItemWidth
    const centerOffset = distanceFromCenter / slideSize

    let scale = 1
    let rotateY = 0
    let translateZ = 0
    let opacity = 1

    const absOffset = Math.abs(centerOffset)

    if (absOffset < 0.01) {
      // 중앙에 정확히 위치
      scale = 1
      rotateY = 0
      translateZ = 0
      opacity = 1
    } else {
      // 중앙에서 멀어질수록 점진적으로 투명도 감소
      if (absOffset <= 1) {
        scale = 1 - absOffset * 0.18
        opacity = 1 - absOffset * 0.45
      } else if (absOffset <= 2) {
        scale = Math.max(0.5, 0.82 - (absOffset - 1) * 0.3)
        opacity = Math.max(0, 0.55 - (absOffset - 1) * 0.55)
      } else {
        opacity = 0
        scale = Math.max(0.5, 0.82 - (absOffset - 1) * 0.3)
      }

      /*
       * 한 칸이 65px(카드 324px × 0.2)뿐이라 카드가 거의 포개져 있다.
       * 그 짧은 거리에 50도 회전과 100px 깊이를 몰아넣으니 손가락을 조금만
       * 움직여도 화면이 크게 요동쳐 어지러웠다. 회전과 깊이를 줄여
       * 넘어가는 동안의 변화량을 낮춘다.
       */
      const maxRotateY = 18
      rotateY = -centerOffset * maxRotateY

      const depth = 36
      translateZ = -absOffset * depth
    }

    // 중앙에 가까울수록 앞에 보이도록 z-index 설정
    const zIndex = Math.round(1000 - Math.abs(centerOffset) * 100)

    return {
      position: 'absolute',
      left: `${itemX}px`,
      top: '50%',
      width: `${itemWidth}px`,
      opacity,
      zIndex,
      transform: `perspective(1000px) rotateY(${rotateY}deg) scale(${scale}) translateZ(${translateZ}px) translateY(-50%)`,
      transformOrigin: 'center center',
      transformStyle: 'preserve-3d',
    }
  }

  /*
   * 어떤 칸이 '가운데'인가.
   *
   * 드래그 중에는 손가락을 따라 실시간으로 바꾸지 않는다. 조금만 움직여도
   * 좌우 카드의 내용이 켜졌다 꺼졌다 하기 때문이다. 손을 뗀 뒤 스냅이
   * 끝난 위치로만 바뀐다.
   */
  const centeredDisplayIndex = isDragging
    ? Math.round(-dragStartOffset / totalItemWidth)
    : Math.round(-currentOffset / totalItemWidth)

  const getVisibleIndices = (): Array<{
    displayIndex: number
    itemIndex: number
  }> => {
    const result: Array<{ displayIndex: number; itemIndex: number }> = []
    // 드래그 제한이 한 칸이므로 현재 기준 좌우 2칸씩만 렌더링
    const visibleRange = 2

    // currentOffset = 0일 때 displayIndex 0이 중앙에 위치
    // currentDisplayIndex는 현재 중앙에 있는 displayIndex를 계산
    const currentDisplayIndex = Math.round(-currentOffset / totalItemWidth)

    for (let i = -visibleRange; i <= visibleRange; i++) {
      const displayIndex = currentDisplayIndex + i
      let itemIndex = displayIndex

      if (loop && items.length > 0) {
        itemIndex =
          ((displayIndex % items.length) + items.length) % items.length
      } else {
        if (displayIndex < 0 || displayIndex >= items.length) continue
        itemIndex = displayIndex
      }

      const existing = result.find((r) => r.displayIndex === displayIndex)
      if (!existing) {
        result.push({ displayIndex, itemIndex })
      }
    }

    // 중앙에 가까운 아이템을 나중에 렌더링하여 앞에 보이도록 정렬
    return result.sort((a, b) => {
      const offsetA = Math.abs(a.displayIndex - currentDisplayIndex)
      const offsetB = Math.abs(b.displayIndex - currentDisplayIndex)
      return offsetA - offsetB
    })
  }

  return (
    <div
      ref={containerRef}
      className="w-full h-full relative overflow-hidden"
      data-swipe-back-ignore
      style={{
        height: viewportHeight,
        minHeight: `${minViewportHeightPx}px`,
        perspective: '1000px',
        perspectiveOrigin: 'center center',
        userSelect: 'none',
        WebkitUserSelect: 'none',
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      {getVisibleIndices().map(({ displayIndex, itemIndex }) => {
        const item = items[itemIndex]
        const style = getItemStyle(displayIndex)
        // 스냅이 끝난 위치 기준으로 가운데를 정한다. 드래그 중에 바뀌면
        // 손가락을 조금만 움직여도 내용이 나타났다 사라진다.
        const rel = displayIndex - centeredDisplayIndex
        const position: 'left' | 'center' | 'right' =
          rel === 0 ? 'center' : rel < 0 ? 'left' : 'right'
        return (
          <div
            key={`${displayIndex}-${itemIndex}`}
            style={{
              ...style,
              userSelect: 'none',
              WebkitUserSelect: 'none',
            }}
            className={isDragging ? 'cursor-grabbing' : 'cursor-grab'}
          >
            {renderItem(item, itemIndex, style, position)}
          </div>
        )
      })}
    </div>
  )
}

export default SlideWidget
